import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, Route, Routes, useLocation, Navigate } from "react-router-dom";

import { RouteList } from "@plannel/routeList";
import Box from "@mui/material/Box";

// components
import Navbar from "@plannel/components/Navbar";
import Sidebar from "@plannel/components/Sidebar";
import TabContainer from "@plannel/components/TabContainer"
import Snackbar from "@plannel/components/Snackbar";
import AgentChatHost from "@plannel/components/agent/AgentChatHost";
import { AgentChatHostProvider } from "@plannel/components/agent/AgentChatHostContext";
import { AgentActionProvider } from "@plannel/components/agent/AgentActions";
import { AgentChatEngineProvider } from "@plannel/components/agent/AgentChatEngineContext";
import WorkflowPanel from "@plannel/components/WorkflowPanel";
import MenuAccessContext from "@plannel/hooks/useMenuAccess";

// main tabs page
import tabMenuList from "@plannel/pages/TabMenuList"
// 메일의 안건 링크를 받는 곳. import 만으로 주소에서 id 를 훔쳐 둔다(모듈 주석 참고)
import {
  MEETING_AGENDA_MENU_KEY, clearPendingAgendaId, peekPendingAgendaId
} from "@plannel/pages/collaboration/agendaDeepLink"

//redux
import { useDispatch, useSelector } from "react-redux";
import reduxUtil from "@plannel/utils/redux-util";
import { isEmpty, isEqual, map } from "lodash";

import companyService from "@plannel/services/data/company-service";
import p13nService from "@plannel/services/system/p13n-service";
import debug from "@plannel/utils/debug-util";

// system management pages
import Home from "@plannel/pages/Home";
import Help from "@plannel/pages/Help";
import Signin from "@plannel/pages/system/Signin";
import Signup from "@plannel/pages/system/Signup";
import MailAuthentication from "@plannel/pages/system/MailAuthentication";
import NotFound from "@plannel/pages/system/NotFound";
import ChangePassword from "@plannel/pages/system/ChangePassword"
import TwoFactorAuthentication from "@plannel/pages/system/TwoFactorAuthentication"
import HelpSignin from "@plannel/pages/system/HelpSignin";
import ForgotPassword from "@plannel/pages/system/ForgotPassword"
import ResetPassword from "@plannel/pages/system/ResetPassword"
import ColorUtil from "@plannel/pages/utils/color-util";
import menuCatalogUtil from "@plannel/pages/utils/menu-catalog-util";
import agentScreenLinkPresets from "@plannel/pages/utils/agent-screen-link-presets";
import userService from "@plannel/services/system/user-service";

import { languages } from "@plannel/utils/i18n";
import { getHistoryState } from "@plannel/redux/modules/HistoryState";
import { pushVisitedTab, getVisitedTabStack } from "@plannel/redux/modules/NavHistoryState";
import getSymbolFromCurrency from 'currency-symbol-map';

import "@plannel/App.scss"
import authService from "./services/system/auth-service";
import { PERMISSION_DENIED_EVENT, SESSION_EXPIRED_EVENT } from "@plannel/services/utils/rest-api";
import { useTranslation } from "react-i18next";

const CLICKED_MENU = "CLICKED_MENU";
/*
 * 홈(랜딩)의 방문 이력 key. 홈은 탭이 아니라 라우트(/home)지만 **이동 이력에서는 1급 항목**이다.
 *   빼 두면 홈에서 Agent Chat 으로 다른 화면에 간 뒤 "돌아가기"가 홈이 아니라, 탭 상태가
 *   persist 로 복원돼 활성으로 남아 있던 옛 탭을 가리킨다(사용자가 이번에 연 적 없는 화면).
 */
const HOME_NAV_KEY = "__HOME__";
const COMMON = "COMMON";
const defaultCurrency = "USD";
const { snackBarMessageColor } = ColorUtil;

function App() {
  /*
   * 정적 `import { t } from "i18next"` 가 아니라 훅을 쓴다.
   * App 은 React.memo 로 감싸여 있어 부모 리렌더로는 갱신되지 않는데, 정적 t 는 언어 변경을
   * 구독하지 않으므로 currentTabTitle(= Agent Chat 의 화면명 칩)이 옛 언어로 굳는다.
   * memo 는 부모 props 기준이라 내부 훅 구독은 그대로 동작한다(리렌더만, remount 아님).
   */
  const { t, i18n } = useTranslation();
  const userData = JSON.parse(localStorage.getItem("user"));
  const settingsData = JSON.parse(localStorage.getItem("settings"));
  const settings = {
    tenant: "",
    username: "",
    lang: languages.EN_US,
    rmCheck: false,
    sbcollapsed: false
  };

  if (localStorage?.settings) {
    settings.tenant = settingsData?.tenant ? settingsData.tenant : "";
    settings.username = settingsData?.username ? settingsData.username : "";
    settings.lang = settingsData?.lang ? settingsData.lang : languages.EN_US;
    settings.rmCheck = settingsData?.rmCheck ? settingsData.rmCheck : false;
    settings.sbcollapsed = settingsData?.sbcollapsed ? settingsData.sbcollapsed : false;
  }

  localStorage.setItem("settings", JSON.stringify(settings));

  const [showedTabsInfo, setShowedTabsInfo] = useState([]);
  const [selectedTabIdx, setSelectedTabIdx] = useState(-1);
  const [showLogo, setShowLogo] = useState(null);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  // 메뉴 접근 권한 — Agent Chat 이 이동 제안할 화면 목록의 근거이자 이동 실행의 게이트.
  //   조회 실패/미로그인이면 null 로 남겨 카탈로그가 빈 목록이 되게 한다(= 이동 불가, 안전측).
  const [userAccess, setUserAccess] = useState(null);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const pinTabs = useRef([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const historyState = useSelector(getHistoryState);
  const visitedTabStack = useSelector(getVisitedTabStack);
  const reduxDispatch = useDispatch();

  if (userData) {
    const decodedJwt = JSON.parse(atob(userData.accessToken.split(".")[1]));
    if (decodedJwt.exp * 1000 < Date.now()) {
      authService.logout();
      reduxDispatch(reduxUtil.initTabState());
      if (!isEqual(location.pathname, "/")) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.tokenExpired, content: t("MSG_TokenExpired") });
      }
    }
  }

  const getKeys = tabsInfo => {
    return map(tabsInfo, 'key');
  }

  const saveReduxState = useCallback((menu) => {
    let reduxState = reduxUtil.createObjectState(CLICKED_MENU, { mainTitle: null, subTitle: null, leafTitle: null });
    if (menu) {
      const menuInfos = tabMenuList.findLv3Menu(menu.key);
      if (menuInfos?.menuPath && !isEmpty(menuInfos.menuPath)) {
        const currentRootTitle = menuInfos.menuPath.length > 1 ? menuInfos.menuPath[0].menuTitle : tabMenuList.getLv1MenuInfo(menuInfos.lv2Key)?.title;
        const currentSubMenu = menuInfos.menuPath.length > 1 ? menuInfos.menuPath[1].menuCd : tabMenuList.getLv2MenuList(menuInfos.title);
        const currentLeafTitle = menuInfos.title;

        reduxState = reduxUtil.createObjectState(CLICKED_MENU, { mainTitle: currentRootTitle, subMenu: currentSubMenu, leafTitle: currentLeafTitle });
      }
    }

    reduxDispatch(reduxUtil.updateViewState(reduxState));
  }, [reduxDispatch])

  const handleAddTab = useCallback((key) => {
    navigate(RouteList.SCM.path);
    const foundIdx = showedTabsInfo.findIndex(element => element.key === key || element.reduxKey === key);
    let nextIdx = foundIdx;

    let addedTabsInfo = showedTabsInfo;
    if (foundIdx < 0) {
      const foundData = tabMenuList.findLv3Menu(key, true);
      nextIdx = showedTabsInfo.length;

      addedTabsInfo = [...showedTabsInfo, foundData];
      setShowedTabsInfo(addedTabsInfo);
    }

    saveReduxState(addedTabsInfo[nextIdx]);

    const reduxTabState = reduxUtil.createTabsState(getKeys(addedTabsInfo), nextIdx);
    reduxDispatch(reduxUtil.updateTabState(reduxTabState));

    setSelectedTabIdx(nextIdx);
  }, [navigate, showedTabsInfo, saveReduxState, reduxDispatch]);

  // 화면 전환을 **앱이 시켰는지**(챗 이동·화면 안의 링크) 사용자가 직접 눌렀는지 구분한다.
  //   Agent Chat 의 "OO 으로 돌아가기" 칩은 데려온 쪽이 책임지고 되돌려 주는 장치라,
  //   사용자가 스스로 메뉴를 골라 나간 경우엔 제안할 이유가 없다(그건 그냥 그 화면을 보러 간 것).
  //   programmatic 이동은 대상 key 를 미리 적어 두고, 활성 탭이 그 key 로 바뀌는 순간에만 인정한다.
  const pendingAppNavKeyRef = useRef(null);
  const navByAppRef = useRef(false);
  const appNavigate = useCallback((key) => {
    pendingAppNavKeyRef.current = key;
    handleAddTab(key);
  }, [handleAddTab]);

  /*
   * 화면 하나의 접근 권한(사이드바와 같은 규칙). 화면 안의 링크를 숨기는 근거이자 이동의 마지막 관문.
   *   userAccess 는 아래 useEffect 에서 채워진다 — 조회 전(null)에는 전부 false(안전측).
   *
   * ⚠️ ref 로 읽어 **함수 정체성을 고정**한다. 소비처 중에는 ag-grid 의 cellStyle/onCellClicked
   *   처럼 그리드가 한 번 캡처하고 다시 안 읽는 콜백이 있어(예: Validation.js 의 planScopeRef 주석),
   *   userAccess 가 바뀔 때마다 새 함수를 주면 옛 클로저가 "권한 없음"에 굳는다.
   *   컨텍스트 값 자체는 userAccess 로 갱신되므로 JSX 로 숨기는 쪽은 정상적으로 다시 렌더된다.
   */
  const userAccessRef = useRef(null);
  userAccessRef.current = userAccess;
  const isPermitted = useCallback(
    (key) => menuCatalogUtil.isMenuPermitted(userAccessRef.current, key), []);
  const menuAccess = useMemo(() => ({ userAccess, isPermitted }), [userAccess, isPermitted]);

  /*
    세션 만료 — restApi 응답 인터셉터가 401 을 보고 쏜다(services/utils/rest-api.js).

    위쪽의 렌더 시점 검사(decodedJwt.exp)만으로는 부족하다. 그건 **렌더가 일어나야** 도는데,
    화면을 열어 둔 채 만료되면 렌더가 없어 아무도 알려주지 않는다. 그 상태에서 뭔가를 누르면
    요청만 조용히 401 로 죽어 "데이터가 없다"처럼 보인다.
    처리는 그 검사와 동일하게 — 로그아웃 + 탭 상태 초기화 + 만료 안내.
    (logout 이 localStorage 의 user 를 지우고, 이 setState 가 부른 재렌더에서 userData 가
     null 이 되며 라우팅이 로그인으로 보낸다 — 렌더 시점 검사와 같은 경로다.)
  */
  useEffect(() => {
    const onSessionExpired = () => {
      authService.logout();
      reduxDispatch(reduxUtil.initTabState());
      setSnackInfo({ open: true, severity: snackBarMessageColor.tokenExpired, content: t("MSG_TokenExpired") });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [reduxDispatch, t]);

    // 권한 거절 — 로그인은 그대로 두고 안내만. 화면이 놓친 쓰기 버튼을 서버가 막았을 때 사용자가 이유를 알게 한다
  useEffect(() => {
    const onPermissionDenied = () => {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_PermissionDenied") });
    };
    window.addEventListener(PERMISSION_DENIED_EVENT, onPermissionDenied);
    return () => window.removeEventListener(PERMISSION_DENIED_EVENT, onPermissionDenied);
  }, [t]);

  /*
   * 화면 안의 링크로 들어오는 이동의 공용 관문.
   *
   * 링크는 각 화면에서 권한 없는 것을 아예 렌더하지 않는 게 원칙이지만(useMenuAccess), 링크가
   *   79곳이라 새 링크에서 가드가 빠질 수 있다. 여기서 한 번 더 막아 두면 그 누락이 "테넌트에
   *   없는 화면이 탭으로 열린다"까지 가지 않는다. 사이드바와 같은 규칙으로 판정한다.
   */
  const navigateIfPermitted = useCallback((key) => {
    if (key == null) return;
    if (!isPermitted(key)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_PermissionDenied") });
      return;
    }
    appNavigate(key);
  }, [appNavigate, isPermitted, t]);

  // 다른 화면에서 메뉴 탭 열기 요청(예: AI 시나리오 → 수요계획 프로세스 관리).
  //   window.dispatchEvent(new CustomEvent("plannel:openMenu", { detail: { key } }))
  useEffect(() => {
    const onOpenMenu = (e) => navigateIfPermitted(e?.detail?.key);
    window.addEventListener("plannel:openMenu", onOpenMenu);
    return () => window.removeEventListener("plannel:openMenu", onOpenMenu);
  }, [navigateIfPermitted]);

  /*
   * 메일의 안건 링크(`/?agenda=123`)로 들어온 경우 회의 안건 탭을 대신 열어 준다.
   *
   * 주소를 훔쳐 두는 일은 agendaDeepLink 모듈이 import 시점에 이미 해 두었다 — 여기서는
   * 로그인과 권한이 준비된 뒤에 탭만 연다. 어떤 안건을 열지는 그 화면이 집어간다.
   *
   * userAccess 를 기다리는 이유: 권한이 오기 전에는 isPermitted 가 전부 false 라(안전측),
   * 먼저 판정하면 권한이 있는 사람에게도 "권한 없음" 이 뜬다.
   */
  useEffect(() => {
    if (!userAccess || !peekPendingAgendaId()) return;
    navigateIfPermitted(MEETING_AGENDA_MENU_KEY);
    // 권한이 없으면 화면이 열리지 않아 아무도 집어가지 않는다. 그대로 두면 다음 로그인 때 또 뜬다
    if (!isPermitted(MEETING_AGENDA_MENU_KEY)) {
      clearPendingAgendaId();
    }
  }, [userAccess, navigateIfPermitted, isPermitted]);

  // 메뉴 접근 권한 조회 — Sidebar·WorkflowPanel 과 같은 API 를 각자 부른다(공유 store 없음).
  useEffect(() => {
    // 익명 세션은 조회할 대상이 없으니 조회가 끝난 것으로 본다.
    //   미확정으로 두면 peek 이 계속 통과한다. 권한은 null 그대로라 판정은 전부 거부다.
    if (!userData?.username) { setAccessLoaded(true); return; }
    userService.getUserAccessRoles({ username: userData.username })
      .then((response) => setUserAccess(response.data))
      .catch((e) => { debug(e); })
      // 실패해도 조회는 끝난 것으로 본다. 재시도가 없으므로 여기서 확정하지 않으면
      //   '로딩 중'이 영원히 풀리지 않아 peek 이 계속 통과한다.
      .finally(() => setAccessLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.username]);

  // 이동 가능한 화면 목록 — 권한 필터 통과분만. 언어가 바뀌면 표시명이 달라지므로 다시 만든다.
  const permittedMenus = useMemo(
    () => menuCatalogUtil.buildPermittedLeafMenus(userAccess, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userAccess, t, i18n.language]);
  const permittedKeys = useMemo(() => menuCatalogUtil.permittedKeySet(permittedMenus), [permittedMenus]);

  // 활성 탭 방문 이력 기록 — Agent Chat "이전 화면으로 돌아가기"의 근거.
  //   탭 추가·탭바 클릭·닫기 보정 등 모든 활성화 경로가 selectedTabIdx 변경으로 수렴하므로 여기 한 곳이면 충분.
  useEffect(() => {
    // 홈에 있으면 탭 상태와 무관하다 — 활성 탭은 남아 있어도 사용자가 보고 있는 화면이 아니다.
    if (isEqual(location.pathname, RouteList.Home.path)) {
      navByAppRef.current = false;   // 홈은 챗이 데려간 화면이 아니다(복귀 칩을 띄우지 않는다)
      reduxDispatch(pushVisitedTab(HOME_NAV_KEY));
      return;
    }
    // 업무 탭 화면이 아니면(로그인·도움말 등) 이동 이력 대상이 아니다.
    if (!isEqual(location.pathname, RouteList.SCM.path)) return;
    const cur = showedTabsInfo[selectedTabIdx];
    if (cur) {
      const curKey = cur.key ?? cur.reduxKey;
      // 이 활성화가 방금 요청한 programmatic 이동인가. 예약 key 는 **한 번만** 쓰고 지운다 —
      //   남겨 두면 나중에 사용자가 그 화면으로 직접 돌아왔을 때도 앱이 데려온 것으로 오인한다.
      navByAppRef.current = pendingAppNavKeyRef.current != null
        && (pendingAppNavKeyRef.current === curKey || pendingAppNavKeyRef.current === cur.reduxKey);
      pendingAppNavKeyRef.current = null;
      reduxDispatch(pushVisitedTab(curKey));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabIdx, JSON.stringify(showedTabsInfo), location.pathname]);

  // Agent Chat 화면 이동 요청 — 챗이 메뉴 지식을 직접 갖지 않도록(순환 import 방지) App 이 처리한다.
  //   dispatchEvent 는 동기라 detail.onResult 로 성공 여부·화면명을 즉시 돌려줄 수 있다.
  //   detail: { back?: true } (직전 화면) | { name: "메뉴명" } | { key: 메뉴키 } (이동)
  //         | { current: true } (지금 보고 있는 화면 조회 — 이동 없음)
  //         | { catalog: true } (이동 가능한 화면 목록 조회 — 챗이 LLM 에 실어 보낸다)
  //         | { search: "텍스트" } (이름 부분일치 후보 조회 — 되묻기용)
  //   이동 요청에 params 를 함께 주면(챗의 '관련 화면' 링크) 대화에서 쓰던 조회 조건
  //     { item_cd, loc_cd, customer_cd, resource_cd, from_date, to_date } 을 대상 화면의
  //     검색조건(viewState)으로 심고 연다. 화면별 필드 번역은 agent-screen-link-presets 가 맡는다.
  //   onResult({ ok, title, key, applied?, ... }) / 권한 밖이면 { ok:false, reason:"forbidden" }
  //     applied = 조건이 실제로 화면에 먹었는가(활성 탭은 리마운트가 없어 안 먹을 수 있다).
  useEffect(() => {
    const menuLabel = (m) => (m?.skipI18n ? m?.title : t(m?.title));
    const atHome = isEqual(location.pathname, RouteList.Home.path);
    // 홈은 메뉴 트리에 없으므로 응답을 직접 만든다 — 탭과 같은 모양이라 소비처는 구분할 필요가 없다.
    const homeResult = () => ({
      ok: true, key: HOME_NAV_KEY, title: t("home"), path: [], lv2Key: null,
      reduxKey: null, lv1Key: null,
      navByApp: navByAppRef.current,
    });
    const onNavScreen = (e) => {
      const { back, anchor, name, key, current, catalog, search, params, onResult } = e?.detail ?? {};
      let menu = null;
      if (catalog) {
        onResult?.({ ok: true, items: permittedMenus });
        return;
      }
      if (search) {
        onResult?.({ ok: true, items: menuCatalogUtil.searchByName(permittedMenus, search) });
        return;
      }
      if (current) {
        // 지금 활성화된 탭. 챗이 백엔드에 "사용자가 보고 있는 화면"을 알리는 유일한 경로다 —
        //   이게 없으면 챗이 직전 모듈이나 기본 모듈을 자기 화면으로 착각해 답한다.
        //   back/anchor 는 현재 탭을 **제외**하므로 별도 분기가 필요하다.
        //   ⚠️ 홈에서는 탭을 보면 안 된다 — 활성 탭이 persist 로 남아 있어 보고 있지도 않은
        //      화면을 현재 화면이라고 답한다(챗이 그 화면 기준으로 답하는 오답의 원인).
        if (atHome) { onResult?.(homeResult()); return; }
        const curTab = showedTabsInfo[selectedTabIdx];
        const curKey = curTab ? (curTab.key ?? curTab.reduxKey) : null;
        const found = curKey != null ? tabMenuList.findLv3Menu(curKey, true) : null;
        if (found) {
          onResult?.({
            ok: true,
            key: found.key ?? found.reduxKey,
            title: menuLabel(found),
            lv2Key: found.lv2Key,
            // 화면의 업무 영역(수요계획·재고계획 …)을 챗이 판정하는 재료. 메뉴 지식은 여기
            //   남기고 챗 모듈 매핑은 agent 계층이 한다(screenChatModule.js).
            //   key 는 숫자 우선이라 reduxKey 가 응답에서 사라지므로 따로 싣는다.
            reduxKey: found.reduxKey,
            // 화면이 viewStates 에 쓰는 키. 챗이 화면 조건을 읽는다(대부분 reduxKey 와 같다).
            viewName: found.component?.props?.viewName ?? found.reduxKey,
            lv1Key: found.menuPath?.[0]?.menuCd ?? null,
            // 이 화면에 온 경로가 앱 주도(챗 이동·화면 안 링크)인가 = 복귀 제안의 유일한 근거.
            navByApp: navByAppRef.current,
            // lv1 > lv2 > lv3 표시명. menuPath 는 findLv3Menu 가 이미 그 순서로 뒤집어 둔다.
            path: (found.menuPath ?? [])
              .map((p) => (p?.skipI18n ? p?.menuTitle : t(p?.menuTitle)))
              .filter(Boolean),
          });
        } else {
          onResult?.({ ok: false });
        }
        return;
      }
      if (back || anchor) {
        // back   = 스택에서 마지막으로 방문한 '현재 활성 탭과 다른' 탭 (직전 화면).
        // anchor = 그중 '시나리오 플래닝 계열(lv2=AI_SCENARIO_PLANNING)' 만 대상 — 챗의 주 무대라
        //          여러 화면을 거쳐도 항상 시나리오 플래닝으로 복귀할 수 있게 하는 기준점.
        //   스택 push(effect)가 탭 전환 렌더보다 늦게 도는 순간이 있어, 단순 stack[len-2] 는
        //   전환 직후 현재 화면을 이전 화면으로 잘못 가리킬 수 있다(간헐 라벨 오류) — 현재 탭
        //   기준으로 걸러 계산하면 push 전/후 어느 시점이든 답이 같다.
        const curTab = showedTabsInfo[selectedTabIdx];
        const curKey = atHome ? HOME_NAV_KEY : (curTab ? (curTab.key ?? curTab.reduxKey) : null);
        const pickKey = [...visitedTabStack].reverse().find((k) => {
          if (k === curKey) return false;
          // 홈은 '직전 화면'은 될 수 있어도 시나리오 플래닝 앵커는 아니다.
          if (k === HOME_NAV_KEY) return !anchor;
          if (!anchor) return true;
          return tabMenuList.findLv3Menu(k, true)?.lv2Key === "AI_SCENARIO_PLANNING";
        });
        if (pickKey === HOME_NAV_KEY) {
          // 홈 복귀는 탭 이동이 아니라 라우트 이동이다. 열어 둔 탭은 그대로 둔다
          //   (Navbar 홈 버튼과 달리 탭 상태를 초기화하지 않는다 — 보던 것을 지울 이유가 없다).
          if (!e?.detail?.peek) {
            pendingAppNavKeyRef.current = null;
            navigate(RouteList.Home.path);
          }
          onResult?.(homeResult());
          return;
        }
        if (pickKey != null) menu = tabMenuList.findLv3Menu(pickKey);
      } else if (key != null) {
        menu = tabMenuList.findLv3Menu(key, true);
      } else if (name) {
        // 카탈로그(권한 통과분)로 먼저 해석하고, 못 찾으면 전체 트리로 폴백한다.
        //   폴백 결과도 아래 권한 게이트를 통과해야 하므로 권한 밖 화면은 열리지 않는다.
        const hit = menuCatalogUtil.findByName(permittedMenus, name);
        menu = hit ? tabMenuList.findLv3Menu(hit.key, true) : tabMenuList.findLv3MenuByName(name, t);
      }
      // 지목 이동(key/name)은 권한을 다시 확인한다 — 카탈로그를 우회한 요청·직접 타이핑 경로 모두 여기서 막힌다.
      //   back/anchor 는 이미 방문했던 탭이므로 대상이 아니다.
      if (menu && (key != null || name)) {
        const targetKey = menu.key ?? menu.reduxKey;
        if (!permittedKeys.has(targetKey)) {
          // 권한 조회가 끝나기 전에는 peek 을 거절하지 않는다.
          //   permittedKeys 가 아직 비어 있어 모든 화면이 권한 밖으로 보이고, 그 사이 만들어지는
          //   챗의 관련 화면 링크가 통째로 사라진다. 조회가 끝났으면 진짜 권한 없음이라 그대로 막는다.
          //   peek 은 이동이 없어 통과시켜도 화면이 열리지 않고, 클릭 시 정식 이동이 권한을 다시 본다.
          if (e?.detail?.peek && !accessLoaded) {
            onResult?.({
              ok: true, pending: true, key: targetKey, title: menuLabel(menu),
              reduxKey: menu.reduxKey, lv2Key: menu.lv2Key ?? null,
              applied: false, staleTab: false,
            });
            return;
          }
          onResult?.({ ok: false, reason: "forbidden" });
          return;
        }
      }
      if (menu) {
        // 조회 조건 인계 — 대화에서 쓰던 품목·거점·기간을 대상 화면의 검색조건으로 미리 심는다.
        //   화면마다 필드명·형태가 달라 번역은 프리셋 카탈로그가 맡는다(agent-screen-link-presets).
        //   removeViewState 를 먼저 부르는 이유: updateViewState 는 merge 라 이전 방문의 형제
        //   조건(버전·다른 검색어)이 살아남아 결과를 과도하게 거른다(floNavActions.js 헤더 참조).
        const viewName = menu.reduxKey;
        const viewState = (params && viewName)
          ? agentScreenLinkPresets.buildLinkViewState(viewName, params) : null;
        if (viewState && !e?.detail?.peek) {
          reduxDispatch(reduxUtil.removeViewState({ viewName }));
          reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, viewState)));
        }
        // peek: 이동하지 않고 대상 존재·이름·키만 조회 (복귀 제안 칩 라벨·중복 제거용)
        //   실제 이동은 appNavigate — 챗이 데려간 화면이어야 그 화면에서 복귀 칩이 뜬다.
        if (!e?.detail?.peek) appNavigate(menu.key ?? menu.reduxKey);
        // 대상이 이미 활성 탭이면 리마운트가 없어 위 viewState 를 아무도 읽지 않는다.
        //   수신 리스너를 가진 화면만 이벤트로 재조회를 깨운다.
        const refreshEvt = viewState && !e?.detail?.peek
          && agentScreenLinkPresets.LINK_REFRESH_EVENTS[viewName];
        if (refreshEvt) window.dispatchEvent(new CustomEvent(refreshEvt, { detail: viewState }));
        // ⚠️ 결과를 applied 하나로 표현하지 않는다. 챗이 안내 문구를 고르려면 세 상태를
        //   구분해야 하는데, applied=false 하나만 주면 "심을 조건이 없었다"와 "활성 탭이라
        //   무시됐다"가 같아 보인다. 실제로 그 혼동 때문에 닫혀 있던 화면을 열고도
        //   "이미 열려 있어요"라고 답했다(사용자 신고). 탭 상태는 App 만 알므로 여기서 알린다.
        const curTab = showedTabsInfo[selectedTabIdx];
        const wasActive = !atHome && !!curTab
          && (curTab.key ?? curTab.reduxKey) === (menu.key ?? menu.reduxKey);
        onResult?.({
          ok: true, title: menuLabel(menu), key: menu.key ?? menu.reduxKey,
          // 조건이 실제로 화면에 먹었는가
          applied: !!viewState && (!wasActive || !!refreshEvt),
          // 조건이 있었는데 활성 탭이라 무시됐는가 — 이때만 "다시 조회해 달라"고 안내한다
          staleTab: !!viewState && wasActive && !refreshEvt,
        });
      } else {
        onResult?.({ ok: false });
      }
    };
    window.addEventListener("plannel:navScreen", onNavScreen);
    return () => window.removeEventListener("plannel:navScreen", onNavScreen);
    // t 는 언어가 바뀌면 참조가 새로 생긴다. 핸들러가 메뉴명을 t 로 만들므로 리스너를 다시 건다
    //   (remove/add 뿐이라 비용 없음). 빼 두면 옛 클로저가 남아 화면명 응답이 어긋날 수 있다.
  }, [appNavigate, navigate, location.pathname, visitedTabStack, showedTabsInfo, selectedTabIdx, t,
      permittedMenus, permittedKeys, accessLoaded, reduxDispatch]);

  // inner tab close tab
  const handleCloseTab = useCallback((tabData) => {
    const { key, reduxKey } = tabData;
    const foundIdx = showedTabsInfo.findIndex(element => element.key === key);

    const filteredTabsInfo = showedTabsInfo.filter(element => element.key !== key);
    setShowedTabsInfo(filteredTabsInfo);

    let currentIndex = selectedTabIdx;
    if (selectedTabIdx >= foundIdx) {
      if (isEmpty(filteredTabsInfo)) {
        currentIndex = -1;
      } else {
        currentIndex = selectedTabIdx > 0 ? selectedTabIdx - 1 : 0;
      }
      setSelectedTabIdx(currentIndex);

      if (selectedTabIdx === foundIdx) {
        const foundData = filteredTabsInfo[currentIndex];
        saveReduxState(foundData);
      }
    }

    const reduxTabState = reduxUtil.createTabsState(getKeys(filteredTabsInfo), currentIndex);
    reduxDispatch(reduxUtil.updateTabState(reduxTabState));

    const reduxState = reduxUtil.getViewState(reduxKey);
    reduxDispatch(reduxUtil.removeViewState(reduxState));
  }, [showedTabsInfo, selectedTabIdx, reduxDispatch, saveReduxState]);

  const removeReduxState = () => {
    const reduxState = reduxUtil.getViewState(CLICKED_MENU);
    reduxDispatch(reduxUtil.removeViewState(reduxState));
  }

  const getCompanyInfo = () => {
    companyService.get()
        .then(
            (response) => {
              setShowLogo(response);
              const currentUserData = JSON.parse(localStorage.getItem("user"));
              currentUserData.currencyCd = response.data.currencyCd || defaultCurrency;
              currentUserData.currencySymbol = getSymbolFromCurrency(currentUserData.currencyCd);
              localStorage.setItem("user", JSON.stringify(currentUserData));
            },
            (error) => {
              debug(error);
            })
        .catch(e => {
          debug(e);
        });
  }

  // 사용자 차트 팔레트 선호(전역 p13n, Personal Settings 에서 저장) 로드 →
  //   ColorUtil 활성 팔레트 세팅 + localStorage 캐시(다음 부팅은 동기 로드로 첫 페인트부터 반영).
  const applySavedChartPalette = () => {
    p13nService.get("PERSONAL_SETTING")
      .then((response) => {
        const id = response?.data?.chartPalette;
        if (!id) return;
        ColorUtil.setActivePalette(id);
        try {
          const s = JSON.parse(localStorage.getItem("settings") || "{}");
          if (s.chartPalette !== id) {
            s.chartPalette = id;
            localStorage.setItem("settings", JSON.stringify(s));
          }
        } catch (e) { debug(e); }
      })
      .catch((e) => debug(e));
  };

  const initTabInfo = useCallback((tabsInfo = [], tabIdx = -1) => {
    setShowedTabsInfo(tabsInfo);
    setSelectedTabIdx(tabIdx);
  }, [])

  /*
   * 저장된 탭 key → 메뉴 leaf. **해석 안 되는 key 는 버린다.**
   *   메뉴에서 삭제된 화면이 저장된 탭 목록에 남아 있을 수 있다(리네임·정리 배포 직후가 대표적).
   *   예전엔 `{...undefined, pin}` 이 그대로 들어가 title·menuPath 가 없는 **유령 탭**이 됐고,
   *   그 탭이 활성화되면 탭바가 빈 항목을 그렸다.
   */
  const findMenuByKeys = (keys, pinTabKeys) => {
    const tabInfos = [];
    for (const key of keys) {
      const foundData = tabMenuList.findLv3Menu(key, true);
      if (!foundData) {
        debug(`tab menu not found, dropped from restored tabs: ${key}`);
        continue;
      }
      // 미출시 화면은 예전에 열어 둔 탭이어도 되살리지 않는다(TabMenuList.isUnreleasedMenu)
      if (tabMenuList.isUnreleasedMenu(key)) {
        debug(`unreleased menu, dropped from restored tabs: ${key}`);
        continue;
      }
      tabInfos.push({ ...foundData, pin: pinTabKeys.includes(key) });
    }

    return tabInfos;
  }

  /*
   * 저장된 탭 인덱스는 목록보다 **오래됐을 수 있다** — 메뉴가 삭제되면 복원된 목록이 짧아지는데
   * 인덱스는 그대로다. 범위를 벗어나면 `showedTabsInfo[idx]` 가 undefined 가 되고,
   * Navbar 의 `...[idx].menuPath` 에서 터져 **앱 셸 전체가 에러 화면으로 대체된다.**
   */
  const clampTabIdx = (idx, len) => (len === 0 ? -1 : Math.min(Math.max(Number(idx) || 0, 0), len - 1));

  const addPin = () => {
    const reduxTabState = reduxUtil.getTabState();
    const pinTabKeys = pinTabs.current;

    if (isEmpty(reduxTabState.tabKeys)) {
      const tapPinInfos = findMenuByKeys(pinTabKeys, pinTabKeys);
      // 저장은 **해석된 목록** 기준으로 — 삭제된 메뉴 key 를 되쓰면 다음 부팅에 같은 문제가 되살아난다
      reduxDispatch(reduxUtil.updateTabState(reduxUtil.createTabsState(getKeys(tapPinInfos), 0)));

      setShowedTabsInfo(tapPinInfos);
      setSelectedTabIdx(isEmpty(tapPinInfos) ? -1 : 0);

      saveReduxState(tapPinInfos[0]);
    } else {
      const { tabKeys, tabIndex } = reduxTabState;

      const reduxTapInfos = findMenuByKeys(tabKeys, pinTabKeys);
      const idx = clampTabIdx(tabIndex, reduxTapInfos.length);
      setShowedTabsInfo(reduxTapInfos);
      setSelectedTabIdx(idx);

      saveReduxState(reduxTapInfos[idx]);

      // 유령 탭을 걷어냈거나 인덱스를 당겼으면 저장 상태도 같이 고친다(안 그러면 매 부팅 재발)
      if (reduxTapInfos.length !== tabKeys.length || idx !== tabIndex) {
        reduxDispatch(reduxUtil.updateTabState(reduxUtil.createTabsState(getKeys(reduxTapInfos), idx)));
      }
    }

    navigate(RouteList.SCM.path);
  }

  const addTab = () => {
    const reduxTabState = reduxUtil.getTabState();
    if (isEmpty(reduxTabState.tabKeys)) {
      initTabInfo();
    } else {
      const { tabKeys, tabIndex } = reduxTabState;
      const reduxTapInfos = findMenuByKeys(tabKeys, []);
      // 저장된 탭이 전부 사라진 메뉴였다면 빈 상태로 되돌린다(아래 location 효과가 Home 으로 보낸다)
      if (isEmpty(reduxTapInfos)) {
        initTabInfo();
        reduxDispatch(reduxUtil.updateTabState(reduxUtil.createTabsState([], -1)));
        return;
      }

      const idx = clampTabIdx(tabIndex, reduxTapInfos.length);
      setShowedTabsInfo(reduxTapInfos);
      setSelectedTabIdx(idx);

      saveReduxState(reduxTapInfos[idx]);

      if (reduxTapInfos.length !== tabKeys.length || idx !== tabIndex) {
        reduxDispatch(reduxUtil.updateTabState(reduxUtil.createTabsState(getKeys(reduxTapInfos), idx)));
      }

      navigate(RouteList.SCM.path);
    }
  }

  const getTabPin = () => {
    p13nService.get(COMMON)
        .then(response => {
          const { data } = response;
          if (data && !isEmpty(data?.tabKeys)) {
            pinTabs.current = data.tabKeys;
            addPin();
          } else {
            addTab();
          }
        })
        .catch(e => {
          debug(e);
        });
  }

  useEffect(() => {
    if (isEqual(location.pathname, RouteList.Help.path)) {
      return;
    }

    if (userData) {
      let routePathExist = false;
      for (const route in RouteList) {
        if (isEqual(location.pathname, RouteList[route].path) && !isEqual(location.pathname, RouteList.NotFound.path)) {
          routePathExist = true;
          break;
        }
      }

      if (routePathExist) {
        if (selectedTabIdx < 0) {
          getCompanyInfo();
          applySavedChartPalette();
          getTabPin();
        }
      }

      if (state?.tabUpdate) {
        getTabPin();
      }

      if (state?.logoUpdate) {
        getCompanyInfo();
        navigate(-1);
      }
    } else {
      initTabInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);


  // Component link Event Handler
  //   화면 안의 드릴다운 링크(예: IP 현황의 긴급 재고 부족 품목 → 목표재고 시뮬레이션 결과)가
  //   updateHistoryState 로 여는 경로다. plannel:openMenu 와 같은 "앱이 데려간 이동"이므로
  //   appNavigate 로 표시해야 그 화면에서 Agent Chat 복귀 칩이 뜬다.
  useEffect(() => {
    if (!isEmpty(historyState)) {
      navigateIfPermitted(historyState);
      reduxDispatch(reduxUtil.removeHistoryState());
    } else if (selectedTabIdx === -1 && isEqual(location.pathname, RouteList.SCM.path)) {
      navigate(RouteList.Home.path);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyState]);

  useEffect(() => {
    if (isEmpty(showedTabsInfo) || selectedTabIdx < 0) {
      if (isEqual(location.pathname, RouteList.SCM.path)) {
        navigate(RouteList.Home.path);
        removeReduxState();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(showedTabsInfo), selectedTabIdx]);

  const curTab = showedTabsInfo[selectedTabIdx];
  const currentTabTitle = curTab && (curTab.skipI18n ? (tabMenuList.getLocalizedTitle(curTab.reduxKey) ?? curTab.title) : t(curTab.title));
  // 챗 패널이 화면 조건을 읽을 viewStates 키 — 화면 컴포넌트의 viewName 이다.
  //   복원된 탭에는 컴포넌트가 없을 수 있어 메뉴 목록에서 다시 찾는다(위 current 분기와 같은 방식).
  const curTabKey = curTab ? (curTab.key ?? curTab.reduxKey) : null;
  const currentScreenKey = useMemo(() => {
    const menu = curTabKey != null ? tabMenuList.findLv3Menu(curTabKey) : null;
    return menu ? (menu.component?.props?.viewName ?? menu.reduxKey ?? null) : null;
  }, [curTabKey]);

  if (!localStorage.getItem("user")) {
    return (
        <Box sx={{ width: "100%", height: "100%" }}>
          <Routes>
            <Route path={RouteList.Root.path} element={<RouteWithoutSidebar component={Signin} />} />
            <Route path={RouteList.Signin.path} element={<RouteWithoutSidebar component={Signin} />} />
            <Route path={RouteList.Signup.path} element={<RouteWithoutSidebar component={Signup} />} />
            <Route path={RouteList.MailAuthentication.path} element={<RouteWithoutSidebar component={MailAuthentication} />} />
            <Route path={RouteList.NotFound.path} element={<RouteWithoutSidebar component={NotFound} />} />
            <Route path={RouteList.ChangePassword.path} element={<RouteWithoutSidebar component={ChangePassword} />} />
            <Route path={RouteList.TwoFactorAuthentication.path} element={<RouteWithoutSidebar component={TwoFactorAuthentication} />} />
            <Route path={RouteList.HelpSignin.path} element={<RouteWithoutSidebar component={HelpSignin} />} />
            <Route path={RouteList.ForgotPassword.path} element={<RouteWithoutSidebar component={ForgotPassword} />} />
            <Route path={RouteList.ResetPassword.path} element={<RouteWithoutSidebar component={ResetPassword} />} />

            <Route path="/*" element={<Navigate to={RouteList.Signin.path} replace />} />
          </Routes>
          <Snackbar
              open={snackInfo.open}
              onClose={() => setSnackInfo({ ...snackInfo, open: false })}
              severity={snackInfo.severity}
              content={snackInfo.content}
          />
        </Box>
    )
  }

  if (isEqual(location.pathname, RouteList.SCM.path)) {
    return (
      // 화면 안의 링크가 대상 화면 권한을 물어볼 수 있게 한다(useMenuAccess) — 권한 없는 링크는 렌더하지 않는다.
      <MenuAccessContext.Provider value={menuAccess}>
        <Box sx={{ display: "flex", height: "100%" }}>
          <Sidebar handleAddTab={handleAddTab} />
          <Box id="pageContainer" sx={{ position: "relative", display: "flex", flexDirection: "column", flexGrow: 1, mx: 1, mb: 1 }}>
            <Box sx={{ flexShrink: 0 }}>
              <Navbar
                  showedTabsInfo={showedTabsInfo}
                  selectedTabIdx={selectedTabIdx}
                  showLogo={showLogo}
                  handleAddTab={handleAddTab}
                  initTabInfo={initTabInfo}
                  permittedMenus={permittedMenus}
              />
            </Box>
            <AppShellBody tabLabel={currentTabTitle} screenKey={currentScreenKey}>
              <Routes>
                <Route path={RouteList.SCM.path} element={
                  <TabsWith
                      showedTabsInfo={showedTabsInfo}
                      setShowedTabsInfo={setShowedTabsInfo}
                      handleCloseTab={handleCloseTab}
                      selectedTabIdx={selectedTabIdx}
                      setSelectedTabIdx={setSelectedTabIdx}
                      saveReduxState={saveReduxState}
                      getKeys={getKeys} />
                } />

                <Route path={RouteList.Help.path} element={<RouteWithoutSidebar component={Help} />} />
              </Routes>
            </AppShellBody>
          </Box>
          <Snackbar
              open={snackInfo.open}
              onClose={() => setSnackInfo({ ...snackInfo, open: false })}
              severity={snackInfo.severity}
              content={snackInfo.content}
          />
        </Box>
      </MenuAccessContext.Provider>
    )
  } else if (isEqual(location.pathname, RouteList.Home.path)) {
    return (
      <MenuAccessContext.Provider value={menuAccess}>
        <Box sx={{ display: "flex", height: "100%" }}>
          <Sidebar handleAddTab={handleAddTab} />
          {/* minWidth 0 — 기본값 auto 면 이 컬럼이 본문 콘텐츠의 min-content 밑으로 못 줄어들어 창이 좁을 때 문서에 가로 스크롤이 생긴다. */}
          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 0, mx: 1, mb: 1 }}>
            <Navbar
                showedTabsInfo={showedTabsInfo}
                selectedTabIdx={selectedTabIdx}
                showLogo={showLogo}
                handleAddTab={handleAddTab}
                initTabInfo={initTabInfo}
                permittedMenus={permittedMenus}
            />
            {/* 홈에서도 탭이 남아 있으면 curTab 이 살아 있다 — 보고 있지 않은 화면의 조건을 싣지 않는다 */}
            <AppShellBody tabLabel={currentTabTitle} screenKey={null}>
              <Box sx={{ flex: 1, minWidth: 0, height: "100%", overflow: "hidden" }}>
                <Routes>
                  <Route path={RouteList.Home.path} element={<RouteWith component={Home} />} />
                  <Route path={RouteList.Help.path} element={<RouteWithoutSidebar component={Help} />} />
                </Routes>
              </Box>
            </AppShellBody>
          </Box>
          <Snackbar
              open={snackInfo.open}
              onClose={() => setSnackInfo({ ...snackInfo, open: false })}
              severity={snackInfo.severity}
              content={snackInfo.content}
          />
        </Box>
      </MenuAccessContext.Provider>
    )
  }

  return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <Routes>
          <Route path={RouteList.NotFound.path} element={<RouteWithoutSidebar component={NotFound} />} />
          <Route path={RouteList.Help.path} element={<RouteWithoutSidebar component={Help} />} />

          <Route path="/*" element={<Navigate to={RouteList.Home.path} replace />} />
        </Routes>
        <Snackbar
            open={snackInfo.open}
            onClose={() => setSnackInfo({ ...snackInfo, open: false })}
            severity={snackInfo.severity}
            content={snackInfo.content}
        />
      </Box>
  )

}

/**
 * AppShellBody — 사이드바/네비바 아래 본문 행. Agent Chat 과 Workflow 패널이 여기 산다.
 *
 * ⚠️ 이 컴포넌트가 존재하는 이유는 레이아웃이 아니라 **패널 인스턴스 보존**이다.
 *   대화 자체는 이제 AgentChatEngineProvider(라우트보다 위)가 들고 있어 안전하지만, 패널의
 *   스크롤 위치·입력 초안·폭은 여전히 이 뷰의 state 다. /scm 과 /home 은 서로 다른 return 문이라,
 *   React 가 fiber 를 재사용하려면
 *   AgentChatHost 가 두 트리에서 **같은 부모의 같은 인덱스**여야 한다 — 예전엔 두 분기에
 *   같은 순서로 적어 두는 것으로 맞췄지만 그건 암묵적이라, 한쪽에 래퍼 <Box> 하나만 끼워도
 *   조용히 깨지고 라우트 이동만으로 대화가 날아간다.
 *   호스트 위치를 여기 한 곳에만 두면 그 정렬이 구조로 보장된다. children 안쪽은 마음대로 바꿔도 된다.
 *   (렌더 결과 DOM 은 추출 전과 동일하다 — 감싸는 Box 를 그대로 옮겼을 뿐이다)
 */
const AppShellBody = ({ tabLabel, screenKey, children }) => (
    <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
      {children}
      <AgentChatHost tabLabel={tabLabel} screenKey={screenKey} />
      <WorkflowPanel />
    </Box>
);

const RouteWithoutSidebar = ({ component: Component }) => {
  return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <Component />
      </Box>
  );
};

const TabsWith = (props) => {
  const { showedTabsInfo, setShowedTabsInfo, handleCloseTab, selectedTabIdx, setSelectedTabIdx, saveReduxState, getKeys } = props;

  return (
      <TabContainer
          data={showedTabsInfo}
          setData={setShowedTabsInfo}
          handleClose={handleCloseTab}
          selectedTabIdx={selectedTabIdx}
          setSelectedTabIdx={setSelectedTabIdx}
          saveReduxState={saveReduxState}
          getKeys={getKeys} />
  );
}

const RouteWith = ({ component: Component }) => {
  return <Component />;
};

const MemoApp = React.memo(App);

/*
  Agent Chat Provider를 App 밖에 두어 다시 마운트되는 것을 방지.

  ⚠️ AgentChatEngineProvider 는 **대화 엔진 본체**다(messages·세션·SSE·busy 가드).
    라우트 분기(/scm · /home)는 App 안에 있으므로, 엔진이 그보다 위에 있어야 화면을 옮겨도
    대화가 살아남는다. 우측 패널(AgentChatHost)과 홈 AI 어시스턴트 카드가 이 하나를 공유한다.
    엔진이 useAgentChatScreen()·useAgentActions() 를 읽으므로 두 Provider 안쪽이어야 한다.
*/
const AppWithAgentChat = (props) => (
    <AgentChatHostProvider>
      <AgentActionProvider>
        <AgentChatEngineProvider>
          <MemoApp {...props} />
        </AgentChatEngineProvider>
      </AgentActionProvider>
    </AgentChatHostProvider>
);

export default AppWithAgentChat;
